/**
 * useAgreements.ts — every read the signing and viewing surfaces make.
 *
 * The queries, and the reason each one exists rather than being folded into another:
 *
 *   useAgreementDocuments  the Documents cards. `get_agreement_documents` is guarded by
 *                          can_access_contract, not by is_admin, precisely so a driver can
 *                          read their own contract's documents.
 *   useAgreementDocument   ONE row, straight from `agreement_documents` under RLS. The RPC
 *                          above is per-contract and `agreement_render` does not return the
 *                          arc columns, which are what the plain-English key terms are
 *                          chosen by, nor `contract_id`, which the ceremony reads the
 *                          driver's own contract insights with.
 *   useAgreementRender     the text, the hash that goes with it, and `path_scope` — the
 *                          folder the ceremony files its signature under, derived by the
 *                          server rather than by this app (see `signatureObjectPath`).
 *   usePendingSignatures   "what is waiting for ME", zero-argument by design: the answer is
 *                          a property of the session, not a filter.
 *   useSignedCopyBlob      the executed PDF's BYTES, for the in-app viewer. Never a file.
 *   useAgreementArcIndex   which in-app document stands for which legacy advance row, so the
 *                          contract insight tabs can open the viewer instead of a storage URL.
 *
 * `(supabase as any).rpc(...)` throughout, as this app does everywhere for RPCs the
 * generated types do not yet know about.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import {
    EMPTY_ARC_INDEX,
    buildAgreementArcIndex,
    type AgreementArcIndex,
    type ArcDocumentRow,
    type LegacyArcRow,
} from '@/lib/agreement-arc-index';
import { isTransportFailure } from '@/lib/signed-copy-failure';
import type { ContractInsightsData } from '@/pages/ContractDetail';

// ─── Shapes ──────────────────────────────────────────────────────────────────

export interface AgreementSignatureSummary {
    party: string;
    role_label: string | null;
    sign_order: number | null;
    status: string | null;
    signer_name: string | null;
    signed_at: string | null;
}

/**
 * One row of `get_agreement_documents`.
 *
 * `title`, `origin`, `completed_at`, the executed PDF's location and `path_scope` are all
 * DERIVED by that function — the columns behind the first four were dropped by
 * `20260801060000_agreement_schema_minimization_and_deposit_links.sql` and the last never
 * was one. They still arrive here, which is exactly why the RPC is what the cards read.
 */
export interface AgreementDocumentSummary {
    id: number;
    doc_type: string;
    reference_no: string;
    title: string;
    status: string;
    origin: string;
    contract_id: number;
    created_at: string;
    frozen_at: string | null;
    completed_at: string | null;
    finalized_at: string | null;
    final_bucket: string | null;
    final_path: string | null;
    signatures: AgreementSignatureSummary[] | null;
    /** `<contract_id>` or `deposit/<contract_deposit_id>`, from `agreement_path_scope`. */
    path_scope: string | null;
    /**
     * ── THE CITED TERMS BOOKLET, WHEN THE SERVER STARTS SAYING SO ─────────────
     * fleet-backend adds `agreement_templates.terms_id` and projects these four columns
     * through this RPC in the PR that follows this one. They are OPTIONAL here because this
     * app ships first: today every row arrives without them, every reader treats them as
     * absent, and nothing waits.
     *
     * THEY ARE A HINT AND NEVER THE ANSWER. The booklet an instrument incorporates is the
     * one its frozen clause 3.1 cites, under the signer's own hash; a foreign key is a row
     * that can be repointed. See `agreement-terms-authority.ts`.
     */
    terms_id?: number | null;
    terms_form_code?: string | null;
    terms_version?: number | null;
    terms_sha256?: string | null;
}

/**
 * The columns the ceremony needs from the row ITSELF, and only ones that still exist.
 *
 * `title` and `completed_at` are NOT among them any more: both were dropped as columns, and
 * PostgREST answers a select naming a column that is gone with a 42703 rather than with a
 * null — so asking for either would break the ceremony outright. The title now comes from
 * `agreement_render` (pinned template) and the completion time is not needed here at all.
 *
 * `contract_id` IS NULLABLE. A deposit agreement created before its deposit was attached to
 * a contract carries none, permanently; the owning contract is derived through the arc at
 * read time. Such a document is never signed in a session — it has no driver_id and its
 * payer has no account — but the type has to admit the shape.
 */
export interface AgreementDocumentRow {
    id: number;
    contract_id: number | null;
    doc_type: string;
    status: string;
    reference_no: string;
    created_at: string;
    frozen_at: string | null;
    finalized_at: string | null;
    loan_agreement_id: number | null;
    insurance_loan_agreement_id: number | null;
    contract_deposit_agreement_id: number | null;
}

const DOCUMENT_COLUMNS =
    'id, contract_id, doc_type, status, reference_no, created_at, frozen_at, ' +
    'finalized_at, loan_agreement_id, insurance_loan_agreement_id, ' +
    'contract_deposit_agreement_id';

/** `agreement_render`'s payload as a driver sees it — the admin-only keys stay null. */
export interface AgreementRenderPayload {
    document_id: number;
    doc_type: string;
    reference_no: string;
    title: string;
    status: string;
    frozen: boolean;
    frozen_at: string | null;
    rendered_md: string;
    content_sha256: string;
    parties: AgreementSignatureSummary[] | null;
    missing: string[] | null;
    values: Record<string, unknown> | null;
    drifted: boolean | null;
    final_bucket: string | null;
    final_path: string | null;
    /**
     * WHERE THIS DOCUMENT'S OBJECTS ARE FILED — `<contract_id>` or
     * `deposit/<contract_deposit_id>`, derived server-side by `agreement_path_scope`.
     *
     * NOT admin-masked, unlike the four keys above it: those are diagnostics about a render,
     * this is the ADDRESS the ceremony composes its own signature path from. It is the one
     * value on this payload the signing step cannot proceed without.
     */
    path_scope: string | null;
}

/** One row of `get_my_pending_signatures`. */
export interface PendingSignature {
    document_id: number;
    contract_id: number;
    doc_type: string;
    reference_no: string;
    title: string;
    party: string;
    role_label: string | null;
    sign_order: number | null;
    signature_status: string;
    document_status: string;
    created_at: string;
    /** The same server-derived scope, so the banner's target needs no second read. */
    path_scope: string | null;
}

// ─── Absence is not an error ─────────────────────────────────────────────────

/**
 * True when the failure is "this subsystem is not on this database yet".
 *
 * The apps and the migration land independently — the plan has both app PRs merging to
 * staging in parallel with, and possibly ahead of, the environment they point at. A
 * Documents page that throws (and reports to Sentry, on every contract, for every driver)
 * until the migration applies would make an expected sequencing gap look like an outage.
 *
 * PGRST202 is PostgREST's "no such function", PGRST205 its "no such table". Anything
 * else — a denied read, a broken argument, a real fault — still throws.
 */
function isSubsystemAbsent(error: unknown): boolean {
    const code = (error as { code?: string } | null)?.code;
    return code === 'PGRST202' || code === 'PGRST205';
}

// ─── Queries ─────────────────────────────────────────────────────────────────

/**
 * `enabled` is offered as an OPTION rather than left to the caller's own `if`, because a
 * caller that needs this list on only one document type has no `if` available: the hook has
 * to be called on every render regardless, and a ceremony for a loan or a deposit would
 * otherwise spend a round trip on an answer it never reads.
 */
export function useAgreementDocuments(
    contractId: number | null | undefined,
    options: { enabled?: boolean } = {},
) {
    return useQuery({
        queryKey: ['agreement-documents', contractId],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_agreement_documents', {
                p_contract_id: contractId,
            });
            if (error) {
                if (isSubsystemAbsent(error)) return [] as AgreementDocumentSummary[];
                throw error;
            }
            return (data ?? []) as AgreementDocumentSummary[];
        },
        // A caller that passes nothing keeps the only condition this hook ever had.
        enabled: !!contractId && options.enabled !== false,
    });
}

export function useAgreementDocument(documentId: number | null | undefined) {
    return useQuery({
        queryKey: ['agreement-document', documentId],
        queryFn: async () => {
            const { data, error } = await (supabase as any)
                .from('agreement_documents')
                .select(DOCUMENT_COLUMNS)
                .eq('id', documentId)
                .maybeSingle();
            if (error) throw error;
            return (data ?? null) as AgreementDocumentRow | null;
        },
        enabled: !!documentId,
    });
}

export function useAgreementRender(documentId: number | null | undefined) {
    return useQuery({
        queryKey: ['agreement-render', documentId],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('agreement_render', {
                p_document_id: documentId,
            });
            if (error) throw error;
            return (data ?? null) as AgreementRenderPayload | null;
        },
        enabled: !!documentId,
        // The frozen text never changes; a stale render is only ever a stale party roster,
        // and the ceremony re-reads the hash from the server on every signing attempt.
        staleTime: 30_000,
    });
}

/**
 * The driver's own contract payload, under the SAME query key `ContractDetail` uses, so a
 * ceremony opened from the Documents list reads it out of the cache rather than off the
 * wire. It is the only place the plain-English key terms can come from: `agreement_render`
 * withholds its merge values from everyone but an admin.
 */
export function useContractInsights(contractId: number | null | undefined) {
    return useQuery({
        queryKey: ['contract-insights', contractId],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_contract_insights', {
                contract_id_param: contractId,
            });
            if (error) throw error;
            return data as ContractInsightsData;
        },
        enabled: !!contractId,
    });
}

export function usePendingSignatures(enabled = true) {
    return useQuery({
        queryKey: ['my-pending-signatures'],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_my_pending_signatures');
            if (error) {
                if (isSubsystemAbsent(error)) return [] as PendingSignature[];
                throw error;
            }
            return (data ?? []) as PendingSignature[];
        },
        enabled,
    });
}

// ─── The executed copy ───────────────────────────────────────────────────────

/**
 * The executed PDF's BYTES, for the in-app viewer.
 *
 * ── WHAT THIS REPLACED, AND WHY THE SHAPE CHANGED WITH IT ─────────────────────
 * This was `useSignedCopyDownload`: the same call, ending in an object URL, an anchor with a
 * `download` attribute and a synthetic click. The owner's ruling (2026-08-21) is that a
 * signed instrument is SHOWN and not handed over, so the file-making half is gone — no object
 * URL, no anchor, no navigation to the bytes, nothing a browser would treat as a download.
 * What is left is an `ArrayBuffer` the sealed viewer paints onto a canvas.
 *
 * It is a QUERY rather than a mutation because it is now a read the page performs to draw
 * itself, not an action a driver takes: the viewer needs the bytes to render at all, and a
 * mutation's "has it been fired yet" is the wrong state for that.
 *
 * The function runs the caller's own JWT against the row policies, so who may read a
 * document is decided in SQL and not here. `functions.invoke` hands back a Blob for an
 * `application/pdf` response, which is why this is not a `fetch` of a signed URL: the
 * archived object lives in a bucket a driver cannot list — and, once fleet-backend's
 * exclusion lands, cannot mint an address in either.
 *
 * ── ONE RETRY, AND ONLY FOR THE FAILURE A RETRY CAN MOVE ──────────────────────
 * `my_signed_copy` is the sole route to these bytes, so a cold edge instance is now the
 * difference between a driver seeing their agreement and seeing an error. A request that
 * never reached a verdict is retried once. A verdict — not finalized, not yours, refused —
 * is an answer, and asking again just spends another round trip on the same no.
 */
export function useSignedCopyBlob(
    documentId: number | null | undefined,
    options: { enabled?: boolean } = {},
) {
    return useQuery({
        queryKey: ['agreement-signed-copy', documentId],
        queryFn: async (): Promise<ArrayBuffer> => {
            const { data, error } = await supabase.functions.invoke('generate-agreement', {
                body: { action: 'my_signed_copy', document_id: documentId },
            });
            if (error) throw error;
            const blob = data as Blob | null;
            if (!(blob instanceof Blob)) {
                throw new Error('AGREEMENT_COPY_NOT_A_PDF: the copy did not arrive as a document');
            }
            return await blob.arrayBuffer();
        },
        enabled: !!documentId && options.enabled !== false,
        // An executed PDF is immutable — it is the sealed instrument — so the bytes are worth
        // exactly one fetch per session. `agreement_finalize` writes it once and never again.
        staleTime: Infinity,
        retry: (failureCount, error) => failureCount < 1 && isTransportFailure(error),
    });
}

// ─── Which document stands for which legacy advance ──────────────────────────

/** The arc columns, off `agreement_documents` itself — no RPC projects them. */
const ARC_COLUMNS = 'id, status, loan_agreement_id, insurance_loan_agreement_id';

/**
 * `loans.id` / `insurance_loans.id` -> the in-app document raised against it.
 *
 * ── THREE READS, ALL OF THEM ORDINARY ─────────────────────────────────────────
 * The contract's documents carry the arc (`loan_agreement_id`), and the legacy tables carry
 * the advance (`loan_id`). Both are readable by the contract's own driver under policies that
 * predate this subsystem, and neither is behind a function that could be missing. The two
 * legacy reads run in parallel and only when an arc was actually found, so a contract with no
 * in-app advance documents — every contract in production today — costs one round trip.
 *
 * ── ABSENCE IS AN EMPTY INDEX, NEVER A THROW ──────────────────────────────────
 * The caller uses this to decide between the in-app viewer and the legacy scan. An index that
 * failed to build must therefore leave the page as it is rather than remove an affordance: a
 * driver whose advance was signed on paper keeps their scan either way, and a driver whose
 * advance was signed in the app sees the same button they see today until the read succeeds.
 */
export function useAgreementArcIndex(contractId: number | null | undefined) {
    return useQuery({
        queryKey: ['agreement-arc-index', contractId],
        queryFn: async (): Promise<AgreementArcIndex> => {
            const { data, error } = await (supabase as any)
                .from('agreement_documents')
                .select(ARC_COLUMNS)
                .eq('contract_id', contractId);
            if (error) {
                if (isSubsystemAbsent(error)) return EMPTY_ARC_INDEX;
                throw error;
            }

            const documents = (data ?? []) as ArcDocumentRow[];
            const loanArcIds = documents
                .map((row) => row.loan_agreement_id)
                .filter((id): id is number => typeof id === 'number');
            const insuranceArcIds = documents
                .map((row) => row.insurance_loan_agreement_id)
                .filter((id): id is number => typeof id === 'number');

            if (loanArcIds.length === 0 && insuranceArcIds.length === 0) return EMPTY_ARC_INDEX;

            const [loanAgreements, insuranceLoanAgreements] = await Promise.all([
                legacyArcRows('loan_agreements', loanArcIds),
                legacyArcRows('insurance_loan_agreements', insuranceArcIds),
            ]);

            return buildAgreementArcIndex({
                documents,
                loanAgreements,
                insuranceLoanAgreements,
            });
        },
        enabled: !!contractId,
        // The arc a document was raised against is write-once in SQL, so this answer is as
        // stable as the document list it is derived from.
        staleTime: 60_000,
    });
}

async function legacyArcRows(table: string, ids: readonly number[]): Promise<LegacyArcRow[]> {
    if (ids.length === 0) return [];
    const { data, error } = await (supabase as any)
        .from(table)
        .select('id, loan_id')
        .in('id', ids);
    // A legacy table this driver cannot read leaves those advances unmatched, which is the
    // same outcome as an advance that was signed on paper — and the correct one.
    if (error) return [];
    return (data ?? []) as LegacyArcRow[];
}
